import { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import {
  ChevronLeft, ChevronRight, Upload, Plus, X, Check,
  MapPin, Clock, DollarSign, Users, Star, Loader2, Globe, Image
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STEPS = [
  { num: 1, label: 'Basics' },
  { num: 2, label: 'Overview' },
  { num: 3, label: 'Day Plan' },
  { num: 4, label: 'Logistics' },
  { num: 5, label: 'Pricing' },
  { num: 6, label: 'Review' },
];

const INCLUSION_OPTIONS = ['Accommodation', 'All meals', 'Park fees', 'Transport', 'Guide', 'Airport transfers', 'Drinking water', 'First aid kit'];
const EXCLUSION_OPTIONS = ['International flights', 'Travel insurance', 'Visa fees', 'Personal expenses', 'Tips', 'Optional activities', 'Laundry', 'Alcoholic drinks'];

export default function CuratedItineraryWizard() {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [coverFile, setCoverFile] = useState(null);
  const [coverPreview, setCoverPreview] = useState(null);
  const [uploadingCover, setUploadingCover] = useState(false);

  const [form, setForm] = useState({
    title: '',
    duration_days: 1,
    description: '',
    highlights: [''],
    customization_note: '',
    daily_plan: [{ day: 1, title: 'Day 1', activities: '', accommodation: '', meals: 'Breakfast, Lunch, Dinner', transport: '' }],
    accommodation_type: '',
    lodge_names: [''],
    transport_type: '',
    guide_included: true,
    transfers_included: true,
    total_price: '',
    price_per_person: '',
    currency: 'USD',
    inclusions: [],
    exclusions: [],
    upgrade_options: [''],
    cover_image: '',
  });

  const updateForm = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleCoverUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCoverFile(file);
    const reader = new FileReader();
    reader.onloadend = () => setCoverPreview(reader.result);
    reader.readAsDataURL(file);
  };

  const addDay = () => {
    const newDay = form.daily_plan.length + 1;
    updateForm('daily_plan', [...form.daily_plan, { day: newDay, title: `Day ${newDay}`, activities: '', accommodation: '', meals: 'Breakfast, Lunch, Dinner', transport: '' }]);
    updateForm('duration_days', newDay);
  };

  const removeDay = (idx) => {
    if (form.daily_plan.length <= 1) return;
    const updated = form.daily_plan.filter((_, i) => i !== idx).map((d, i) => ({ ...d, day: i + 1 }));
    updateForm('daily_plan', updated);
    updateForm('duration_days', updated.length);
  };

  const updateDay = (idx, field, value) => {
    const updated = [...form.daily_plan];
    updated[idx] = { ...updated[idx], [field]: value };
    updateForm('daily_plan', updated);
  };

  const handleSubmit = async () => {
    if (!form.title.trim()) { toast.error('Please enter a title'); return; }
    setSubmitting(true);
    try {
      let coverImageId = form.cover_image;
      if (coverFile) {
        setUploadingCover(true);
        const formData = new FormData();
        formData.append('file', coverFile);
        const uploadRes = await api.post('/files/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
        coverImageId = uploadRes.data.file_id;
        setUploadingCover(false);
      }

      const payload = {
        ...form,
        cover_image: coverImageId,
        total_price: parseFloat(form.total_price) || 0,
        price_per_person: parseFloat(form.price_per_person) || 0,
        highlights: form.highlights.filter(h => h.trim()),
        lodge_names: form.lodge_names.filter(n => n.trim()),
        upgrade_options: form.upgrade_options.filter(o => o.trim()),
      };

      await api.post('/itineraries/curated', payload);
      toast.success('Curated itinerary created successfully!');
      navigate('/operator');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create itinerary');
    } finally { setSubmitting(false); setUploadingCover(false); }
  };

  return (
    <div className="min-h-screen bg-zinc-50">
      {/* Top Bar */}
      <div className="bg-white border-b border-zinc-200 sticky top-0 z-20">
        <div className="max-w-4xl mx-auto px-6 py-3 flex items-center justify-between">
          <button onClick={() => navigate('/operator')} className="flex items-center gap-1.5 text-sm text-zinc-500 hover:text-zinc-900 transition font-medium" data-testid="wizard-back">
            <ChevronLeft size={16} /> Back to Dashboard
          </button>
          <div className="flex items-center gap-1">
            {STEPS.map((s) => (
              <button
                key={s.num}
                onClick={() => setStep(s.num)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition ${
                  s.num === step ? 'bg-zinc-900 text-white' :
                  s.num < step ? 'bg-zinc-200 text-zinc-700' :
                  'bg-zinc-100 text-zinc-400'
                }`}
                data-testid={`wizard-step-${s.num}`}
              >
                {s.label}
              </button>
            ))}
          </div>
          <span className="text-xs text-zinc-400">Step {step} of 6</span>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-3xl mx-auto px-6 py-8">

        {/* Step 1: Basics */}
        {step === 1 && (
          <div className="space-y-6" data-testid="step-basics">
            <div>
              <h2 className="text-xl font-bold text-zinc-900">Trip Basics</h2>
              <p className="text-sm text-zinc-500 mt-1">Give your itinerary a compelling name and cover image.</p>
            </div>

            <div>
              <label className="text-sm font-medium text-zinc-700 block mb-1.5">Title</label>
              <Input value={form.title} onChange={e => updateForm('title', e.target.value)} placeholder="e.g., 5-Day Serengeti Safari Experience" className="rounded-xl border-zinc-200" data-testid="wizard-title" />
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-zinc-700 block mb-1.5">Duration (days)</label>
                <Input type="number" min={1} value={form.duration_days} onChange={e => {
                  const d = Math.max(1, parseInt(e.target.value) || 1);
                  updateForm('duration_days', d);
                  const plan = Array.from({ length: d }, (_, i) => form.daily_plan[i] || { day: i + 1, title: `Day ${i + 1}`, activities: '', accommodation: '', meals: 'Breakfast, Lunch, Dinner', transport: '' });
                  updateForm('daily_plan', plan);
                }} className="rounded-xl border-zinc-200" data-testid="wizard-duration" />
              </div>
              <div>
                <label className="text-sm font-medium text-zinc-700 block mb-1.5">Currency</label>
                <select value={form.currency} onChange={e => updateForm('currency', e.target.value)} className="w-full rounded-xl border border-zinc-200 px-3 py-2 text-sm bg-white focus:outline-none focus:border-zinc-400" data-testid="wizard-currency">
                  <option value="USD">USD</option>
                  <option value="EUR">EUR</option>
                  <option value="GBP">GBP</option>
                  <option value="TZS">TZS</option>
                </select>
              </div>
            </div>

            {/* Cover Image */}
            <div>
              <label className="text-sm font-medium text-zinc-700 block mb-1.5">Cover Image</label>
              {coverPreview ? (
                <div className="relative rounded-2xl overflow-hidden aspect-video bg-zinc-100">
                  <img src={coverPreview} alt="Cover" className="w-full h-full object-cover" />
                  <button onClick={() => { setCoverFile(null); setCoverPreview(null); updateForm('cover_image', ''); }} className="absolute top-3 right-3 w-8 h-8 bg-black/60 rounded-full flex items-center justify-center text-white hover:bg-black/80 transition">
                    <X size={16} />
                  </button>
                </div>
              ) : (
                <label className="flex flex-col items-center justify-center gap-3 rounded-2xl border-2 border-dashed border-zinc-200 py-12 hover:border-zinc-400 transition cursor-pointer" data-testid="cover-upload">
                  <div className="w-12 h-12 rounded-2xl bg-zinc-100 flex items-center justify-center">
                    <Image size={20} className="text-zinc-400" />
                  </div>
                  <div className="text-center">
                    <p className="text-sm font-medium text-zinc-700">Upload cover image</p>
                    <p className="text-xs text-zinc-400 mt-0.5">JPG, PNG or WebP. Recommended 1200x800.</p>
                  </div>
                  <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={handleCoverUpload} />
                </label>
              )}
            </div>
          </div>
        )}

        {/* Step 2: Overview */}
        {step === 2 && (
          <div className="space-y-6" data-testid="step-overview">
            <div>
              <h2 className="text-xl font-bold text-zinc-900">Overview & Highlights</h2>
              <p className="text-sm text-zinc-500 mt-1">Describe the experience and list key highlights.</p>
            </div>

            <div>
              <label className="text-sm font-medium text-zinc-700 block mb-1.5">Description</label>
              <textarea value={form.description} onChange={e => updateForm('description', e.target.value)}
                className="w-full rounded-xl border border-zinc-200 px-4 py-3 text-sm min-h-[140px] resize-none focus:border-zinc-400 focus:ring-0"
                placeholder="Describe what makes this itinerary special..."
                data-testid="wizard-description" />
            </div>

            <div>
              <label className="text-sm font-medium text-zinc-700 block mb-2">Highlights</label>
              <div className="space-y-2">
                {form.highlights.map((h, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <Star size={14} className="text-amber-400 shrink-0" />
                    <Input value={h} onChange={e => { const u = [...form.highlights]; u[i] = e.target.value; updateForm('highlights', u); }}
                      placeholder={`Highlight ${i + 1}`} className="flex-1 rounded-xl border-zinc-200" />
                    {form.highlights.length > 1 && (
                      <button onClick={() => updateForm('highlights', form.highlights.filter((_, j) => j !== i))} className="p-1.5 text-zinc-400 hover:text-red-500 transition">
                        <X size={14} />
                      </button>
                    )}
                  </div>
                ))}
              </div>
              <button onClick={() => updateForm('highlights', [...form.highlights, ''])} className="text-xs text-zinc-500 hover:text-zinc-900 font-medium mt-2 flex items-center gap-1">
                <Plus size={12} /> Add highlight
              </button>
            </div>

            <div>
              <label className="text-sm font-medium text-zinc-700 block mb-1.5">Customization Note (optional)</label>
              <Input value={form.customization_note} onChange={e => updateForm('customization_note', e.target.value)}
                placeholder="e.g., Can be customized for private groups..." className="rounded-xl border-zinc-200" />
            </div>
          </div>
        )}

        {/* Step 3: Day Plan */}
        {step === 3 && (
          <div className="space-y-6" data-testid="step-dayplan">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold text-zinc-900">Day-by-Day Plan</h2>
                <p className="text-sm text-zinc-500 mt-1">{form.daily_plan.length} day{form.daily_plan.length !== 1 ? 's' : ''} planned</p>
              </div>
              <Button onClick={addDay} variant="outline" className="rounded-xl border-zinc-200 text-sm h-9 font-medium" data-testid="add-day-btn">
                <Plus size={14} className="mr-1" /> Add Day
              </Button>
            </div>

            <div className="space-y-4">
              {form.daily_plan.map((day, idx) => (
                <div key={idx} className="bg-white rounded-2xl border border-zinc-200 p-5" data-testid={`day-card-${idx}`}>
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-zinc-900 text-white text-sm font-bold flex items-center justify-center">{day.day}</div>
                      <Input value={day.title} onChange={e => updateDay(idx, 'title', e.target.value)} placeholder="Day title" className="border-0 p-0 h-auto font-semibold text-zinc-900 shadow-none focus-visible:ring-0" />
                    </div>
                    {form.daily_plan.length > 1 && (
                      <button onClick={() => removeDay(idx)} className="p-1.5 text-zinc-400 hover:text-red-500 transition">
                        <X size={14} />
                      </button>
                    )}
                  </div>
                  <div className="grid sm:grid-cols-2 gap-3">
                    <div>
                      <label className="text-[10px] uppercase tracking-wider text-zinc-400 font-bold block mb-1">Activities</label>
                      <textarea value={day.activities} onChange={e => updateDay(idx, 'activities', e.target.value)}
                        className="w-full rounded-lg border border-zinc-200 px-3 py-2 text-sm min-h-[70px] resize-none focus:border-zinc-400 focus:ring-0"
                        placeholder="Morning game drive, afternoon nature walk..." />
                    </div>
                    <div className="space-y-2">
                      <div>
                        <label className="text-[10px] uppercase tracking-wider text-zinc-400 font-bold block mb-1">Accommodation</label>
                        <Input value={day.accommodation} onChange={e => updateDay(idx, 'accommodation', e.target.value)} placeholder="Lodge or camp name" className="rounded-lg border-zinc-200 text-sm" />
                      </div>
                      <div>
                        <label className="text-[10px] uppercase tracking-wider text-zinc-400 font-bold block mb-1">Meals</label>
                        <Input value={day.meals} onChange={e => updateDay(idx, 'meals', e.target.value)} placeholder="Breakfast, Lunch, Dinner" className="rounded-lg border-zinc-200 text-sm" />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Step 4: Logistics */}
        {step === 4 && (
          <div className="space-y-6" data-testid="step-logistics">
            <div>
              <h2 className="text-xl font-bold text-zinc-900">Logistics & Accommodation</h2>
              <p className="text-sm text-zinc-500 mt-1">Transport, accommodation type and lodge details.</p>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-zinc-700 block mb-1.5">Accommodation Type</label>
                <select value={form.accommodation_type} onChange={e => updateForm('accommodation_type', e.target.value)} className="w-full rounded-xl border border-zinc-200 px-3 py-2 text-sm bg-white focus:outline-none focus:border-zinc-400">
                  <option value="">Select type</option>
                  <option value="budget">Budget</option>
                  <option value="mid-range">Mid-Range</option>
                  <option value="luxury">Luxury</option>
                  <option value="camping">Camping</option>
                  <option value="mixed">Mixed</option>
                </select>
              </div>
              <div>
                <label className="text-sm font-medium text-zinc-700 block mb-1.5">Transport Type</label>
                <select value={form.transport_type} onChange={e => updateForm('transport_type', e.target.value)} className="w-full rounded-xl border border-zinc-200 px-3 py-2 text-sm bg-white focus:outline-none focus:border-zinc-400">
                  <option value="">Select transport</option>
                  <option value="4x4 Land Cruiser">4x4 Land Cruiser</option>
                  <option value="Safari Van">Safari Van</option>
                  <option value="Private Vehicle">Private Vehicle</option>
                  <option value="Shared Vehicle">Shared Vehicle</option>
                  <option value="Mixed">Mixed (Land + Domestic Flights)</option>
                </select>
              </div>
            </div>

            <div>
              <label className="text-sm font-medium text-zinc-700 block mb-2">Lodge / Camp Names</label>
              <div className="space-y-2">
                {form.lodge_names.map((name, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <MapPin size={14} className="text-zinc-400 shrink-0" />
                    <Input value={name} onChange={e => { const u = [...form.lodge_names]; u[i] = e.target.value; updateForm('lodge_names', u); }}
                      placeholder={`Lodge ${i + 1}`} className="flex-1 rounded-xl border-zinc-200" />
                    {form.lodge_names.length > 1 && (
                      <button onClick={() => updateForm('lodge_names', form.lodge_names.filter((_, j) => j !== i))} className="p-1.5 text-zinc-400 hover:text-red-500 transition">
                        <X size={14} />
                      </button>
                    )}
                  </div>
                ))}
              </div>
              <button onClick={() => updateForm('lodge_names', [...form.lodge_names, ''])} className="text-xs text-zinc-500 hover:text-zinc-900 font-medium mt-2 flex items-center gap-1">
                <Plus size={12} /> Add lodge
              </button>
            </div>

            <div className="flex items-center gap-6">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={form.guide_included} onChange={e => updateForm('guide_included', e.target.checked)} className="rounded border-zinc-300" />
                <span className="text-sm text-zinc-700 font-medium">Guide Included</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={form.transfers_included} onChange={e => updateForm('transfers_included', e.target.checked)} className="rounded border-zinc-300" />
                <span className="text-sm text-zinc-700 font-medium">Transfers Included</span>
              </label>
            </div>
          </div>
        )}

        {/* Step 5: Pricing */}
        {step === 5 && (
          <div className="space-y-6" data-testid="step-pricing">
            <div>
              <h2 className="text-xl font-bold text-zinc-900">Pricing & Inclusions</h2>
              <p className="text-sm text-zinc-500 mt-1">Set pricing and what is included/excluded.</p>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-zinc-700 block mb-1.5">Total Price ({form.currency})</label>
                <Input type="number" value={form.total_price} onChange={e => updateForm('total_price', e.target.value)} placeholder="2500" className="rounded-xl border-zinc-200" data-testid="wizard-total-price" />
              </div>
              <div>
                <label className="text-sm font-medium text-zinc-700 block mb-1.5">Price Per Person ({form.currency})</label>
                <Input type="number" value={form.price_per_person} onChange={e => updateForm('price_per_person', e.target.value)} placeholder="625" className="rounded-xl border-zinc-200" />
              </div>
            </div>

            <div>
              <label className="text-sm font-medium text-zinc-700 block mb-2">Inclusions</label>
              <div className="flex flex-wrap gap-2">
                {INCLUSION_OPTIONS.map(opt => (
                  <button key={opt} onClick={() => updateForm('inclusions', form.inclusions.includes(opt) ? form.inclusions.filter(i => i !== opt) : [...form.inclusions, opt])}
                    className={`px-3 py-1.5 rounded-xl text-xs font-semibold border transition ${
                      form.inclusions.includes(opt) ? 'bg-zinc-900 text-white border-zinc-900' : 'bg-white text-zinc-600 border-zinc-200 hover:border-zinc-400'
                    }`}
                  >
                    {form.inclusions.includes(opt) && <Check size={10} className="inline mr-1" />}{opt}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="text-sm font-medium text-zinc-700 block mb-2">Exclusions</label>
              <div className="flex flex-wrap gap-2">
                {EXCLUSION_OPTIONS.map(opt => (
                  <button key={opt} onClick={() => updateForm('exclusions', form.exclusions.includes(opt) ? form.exclusions.filter(i => i !== opt) : [...form.exclusions, opt])}
                    className={`px-3 py-1.5 rounded-xl text-xs font-semibold border transition ${
                      form.exclusions.includes(opt) ? 'bg-red-50 text-red-700 border-red-200' : 'bg-white text-zinc-600 border-zinc-200 hover:border-zinc-400'
                    }`}
                  >
                    {form.exclusions.includes(opt) && <X size={10} className="inline mr-1" />}{opt}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Step 6: Review */}
        {step === 6 && (
          <div className="space-y-6" data-testid="step-review">
            <div>
              <h2 className="text-xl font-bold text-zinc-900">Review Your Itinerary</h2>
              <p className="text-sm text-zinc-500 mt-1">Everything looks good? Submit to create your curated itinerary.</p>
            </div>

            {/* Preview Card */}
            <div className="bg-white rounded-2xl border border-zinc-200 overflow-hidden">
              {coverPreview && (
                <div className="aspect-video bg-zinc-100 relative">
                  <img src={coverPreview} alt="Cover" className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                  <div className="absolute bottom-4 left-5 right-5">
                    <h3 className="text-white font-bold text-lg">{form.title || 'Untitled Itinerary'}</h3>
                    <p className="text-white/80 text-sm">{form.duration_days} days &middot; ${parseFloat(form.total_price || 0).toLocaleString()} {form.currency}</p>
                  </div>
                </div>
              )}
              {!coverPreview && (
                <div className="bg-zinc-900 p-5">
                  <h3 className="text-white font-bold text-lg">{form.title || 'Untitled Itinerary'}</h3>
                  <p className="text-zinc-300 text-sm">{form.duration_days} days &middot; ${parseFloat(form.total_price || 0).toLocaleString()} {form.currency}</p>
                </div>
              )}

              <div className="p-5 space-y-4">
                {form.description && (
                  <div>
                    <p className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold mb-1">Overview</p>
                    <p className="text-sm text-zinc-700">{form.description}</p>
                  </div>
                )}

                <div className="grid sm:grid-cols-3 gap-3">
                  <div className="bg-zinc-50 rounded-xl p-3 text-center">
                    <Clock size={16} className="mx-auto text-zinc-400 mb-1" />
                    <p className="text-sm font-bold text-zinc-900">{form.duration_days} days</p>
                  </div>
                  <div className="bg-zinc-50 rounded-xl p-3 text-center">
                    <DollarSign size={16} className="mx-auto text-zinc-400 mb-1" />
                    <p className="text-sm font-bold text-zinc-900">${parseFloat(form.total_price || 0).toLocaleString()}</p>
                  </div>
                  <div className="bg-zinc-50 rounded-xl p-3 text-center">
                    <MapPin size={16} className="mx-auto text-zinc-400 mb-1" />
                    <p className="text-sm font-bold text-zinc-900 capitalize">{form.accommodation_type || '—'}</p>
                  </div>
                </div>

                {form.daily_plan?.length > 0 && (
                  <div>
                    <p className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold mb-2">Day Plan</p>
                    <div className="space-y-2">
                      {form.daily_plan.map((d, i) => (
                        <div key={i} className="flex gap-3">
                          <div className="w-6 h-6 rounded bg-zinc-900 text-white text-[10px] font-bold flex items-center justify-center shrink-0">{d.day}</div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-zinc-900">{d.title}</p>
                            {d.activities && <p className="text-xs text-zinc-500 truncate">{d.activities}</p>}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="grid sm:grid-cols-2 gap-3">
                  {form.inclusions.length > 0 && (
                    <div>
                      <p className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold mb-1.5">Included</p>
                      {form.inclusions.map(i => <p key={i} className="text-sm text-zinc-700 flex items-center gap-1.5"><Check size={11} className="text-emerald-500" />{i}</p>)}
                    </div>
                  )}
                  {form.exclusions.length > 0 && (
                    <div>
                      <p className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold mb-1.5">Excluded</p>
                      {form.exclusions.map(i => <p key={i} className="text-sm text-zinc-500 flex items-center gap-1.5"><X size={11} className="text-red-400" />{i}</p>)}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Navigation */}
        <div className="flex items-center justify-between mt-8 pt-6 border-t border-zinc-200">
          {step > 1 ? (
            <Button onClick={() => setStep(s => s - 1)} variant="outline" className="rounded-xl border-zinc-200 px-6 py-2.5 text-sm font-medium" data-testid="wizard-prev">
              <ChevronLeft size={16} className="mr-1" /> Previous
            </Button>
          ) : <div />}
          {step < 6 ? (
            <Button onClick={() => setStep(s => s + 1)} className="bg-zinc-900 text-white hover:bg-zinc-800 rounded-xl px-6 py-2.5 text-sm font-semibold" data-testid="wizard-next">
              Continue <ChevronRight size={16} className="ml-1" />
            </Button>
          ) : (
            <Button onClick={handleSubmit} disabled={submitting} className="bg-zinc-900 text-white hover:bg-zinc-800 rounded-xl px-8 py-2.5 text-sm font-semibold" data-testid="wizard-submit">
              {submitting ? <><Loader2 size={16} className="animate-spin mr-2" /> {uploadingCover ? 'Uploading...' : 'Creating...'}</> : <><Globe size={16} className="mr-2" /> Create Itinerary</>}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
